import moment from 'moment';

import { featureFlag } from '@@/auto/constants/featureFlags';
import {
  TConfig,
  TConfigInput,
  TConfigParams,
  TGetAddOns,
} from '@@/auto/interfaces';
import { excludeFalse } from '@@/auto/utils/excludeFalse';

import { AUTO_DISCOUNTS } from '../../autoDiscounts';

function ToggleCAConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  return isFeatureEnabled(featureFlag.IS_CA_EXPANSION_ENABLED)
    ? {
        antiTheftCopyConfig: () => ({
          label: 'Anti-theft protection',
          explanationText:
            'Is your vehicle equipped with anti-theft protection such as a car alarm or GPS tracker?',
        }),
        auto: (auto: TConfig['auto']) => {
          return {
            ...auto,
            addOns: {
              options: {
                additional_equipment: {
                  copy: {
                    label: 'Additional Equipment Coverage',
                    description:
                      'This coverage is included if your vehicle has comprehensive or collision coverage.',
                    modalContent: [
                      'Additional equipment coverage means any of the following equipment, which is not the vehicle manufacturer’s standard equipment, or installed by a factory authorized dealer at the time of purchase.',
                      '1. Any video, electronic sound reproducing or transceiving equipment and its component parts.',
                      '2. Any painted or finished surface where the claim exceeds the cost of duplicating the vehicle manufacturer’s standard surface finish.',
                      '3. Tires, wheels or any exterior surface equipment, or modification.',
                      '4. Any engine, transmission or suspension parts, or modification.',
                    ],
                  },
                },
                rental_reimbursement: {
                  copy: {
                    label: 'Rental Reimbursement',
                    description:
                      "While you're without your car after an accident, this coverage helps provide alternate transportation. It's optional but if something happens to your car, ask yourself if you really want to count on your buddy getting you to work.",
                    modalContent:
                      'Rental Reimbursement is optional and provides coverage up to the policy limit for car rental while your vehicle is being repaired or replaced.',
                  },
                },
              },
              alwaysOnOptions: {
                roadside_assistance: {
                  copy: {
                    label: 'Roadside Assistance',
                    description:
                      "Getting stranded with a flat is no fun. Especially if you don't know a jack from a lug wrench.",
                    textAlert:
                      'Lucky for you this Add On is always on with your Toggle Coverage!',
                    modalContent:
                      'Roadside Assistance helps pay the cost of towing and other roadside services like a locksmith, flat tire changing, and battery jumpstart in the event your vehicle is disabled (subject to policy limits and the Service Call conditions).',
                  },
                },
              },
              getAddOns: ({ userAction }: Parameters<TGetAddOns>[0]) => [
                ...excludeFalse([userAction === 'editing-addons'] as const),
                'rental_reimbursement',
                'additional_equipment',
              ],
            },
          };
        },
        autoDiscounts: () => [
          AUTO_DISCOUNTS.affinity,
          AUTO_DISCOUNTS.caAffinity,
          AUTO_DISCOUNTS.superiorDriver,
          AUTO_DISCOUNTS.goodDriver,
          AUTO_DISCOUNTS.multiCarCA,
          AUTO_DISCOUNTS.goodStudent25,
          AUTO_DISCOUNTS.matureDriver50,
          AUTO_DISCOUNTS.antiTheft,
          AUTO_DISCOUNTS.mixedPolicyRating,
        ],
        cancelPolicyConfig: () => ({
          firstSlideCopy: ({
            policyEndDate,
            otherProps,
          }: {
            policyEndDate: string;
            otherProps: Record<string, any>;
          }) => {
            return (
              <div {...otherProps}>
                <p>
                  Your policy is active until{' '}
                  <strong>
                    {moment.utc(policyEndDate).format('MMMM D, YYYY')}
                  </strong>
                  .
                </p>
                <p>
                  If you need to cancel your policy for any reason, you can do
                  it online here.
                </p>
                <p>Cancellations will be subject to a $50 fee.</p>
                <p>
                  Your coverage will end immediately and you’ll be refunded any
                  amount due.
                </p>
              </div>
            );
          },
        }),
        collectEmailViewConfig: (
          collectEmailViewConfig: TConfig['collectEmailViewConfig']
        ) => ({
          ...collectEmailViewConfig,
          subHeader: `We may use your email address for offers relating to this quote, other products or other services`,
        }),
        coverage: () => {
          return {
            bodilyInjury: {
              title: 'Bodily Injury Liability',
              description: `Accidents happen. So do injuries to other people. Help protect yourself in the event you're found at fault.`,
              modalContent: `Bodily Injury (BI) coverage pays for damages you or the driver of your vehicle are legally obligated to pay due to accidental bodily injury to others, and the injury occurred due to a covered loss. This is a mandatory coverage and the limits chosen are the maximum the company will pay for any single occurrence.`,
            },
            collision: {
              title: 'Collision',
              description:
                'Crash, fender-bender, or a mailbox that came out of nowhere -- collision coverage can help. You choose your deductible.',
              textAlertContent: '',
              modalContent: `Collision helps cover the cost of repairs to your car if you've been hit by another vehicle, and can also help if you hit another car or object. Coverage may extend to other cars, such as a dealership loaner, rental car, or a car you borrowed. You pick the deductible and pay this amount before receiving benefits. Benefit payout is based on the actual cash value of your car.`,
            },
            collisionDeductibleWaiver: {
              title: 'Collision Deductible Waiver',
              description:
                'No deductible for accidents caused by an uninsured motorist.',
              textAlertContent:
                'This coverage is only available if Collision is also selected.',
            },
            comprehensive: {
              title: 'Comprehensive',
              description: `Comprehensive kicks in for accidents that aren't a crash – think flood, hail, fire, theft, or a rogue rock cracks your windshield.`,
              textAlertContent: '',
              modalContent: `Comprehensive is an optional coverage that helps you pay to repair or replace a car that's involved in an accident not caused by a collision. For example, losses due to vandalism, theft, or damage by something other than a crash or rolling over. You select the deductible and pay this amount before receiving benefits. Comprehensive benefit payout is based on the actual cash value of your car.`,
            },
            medical: {
              title: 'Medical',
              description:
                'Accidents can be scary, especially when you or someone you love is injured. If that happens, though, this coverage can help lessen the impact to your wallet (or theirs).',
              modalContent:
                'Medical coverage pays reasonable medical and funeral expenses incurred by an insured person on your policy caused by an accident, up to the limit selected and regardless of fault.',
              textAlertContent: '',
            },
            propertyDamage: {
              title: 'Property Damage Liability',
              description:
                "You don't really get to pick which car you accidentally rear-end - whether it's a beater or a bimmer, this coverage can help make sure you're covered.",
              modalContent:
                "Property damage (PD) coverage pays for damages if you or the driver of your vehicle are found legally obligated to pay for accidental property damage to someone else's property due to a covered loss, up to the limit of coverage chosen by you. This is a mandatory coverage.",
            },
            uninsuredMotoristPropertyDamage: {
              description: `Your bumper sticker might say "don't hit me if you're uninsured" but it isn't actually protecting you against that. Don't worry, this coverage can.`,
              modalContent:
                'Uninsured Motorist Property Damage (UMPD) pays, up to the limits chosen, the amount you are legally entitled to recover for physical damages to your insured car sustained in an accident caused by a driver who is not insured.',
              title: 'Uninsured Motorist Property Damage',
            },
            uninsuredMotoristBodilyInjury: {
              title: 'Uninsured Motorist Bodily Injury',
              description:
                "That pain in your neck is not just from the accident - it's also from finding out that the person who hit you has no insurance. This coverage can help.",
              modalContent:
                'Uninsured Motorist Bodily Injury (UMBI) pays the amount you are legally entitled to recover for damages from bodily injury, up to the limit of liability, when an insured person on your policy is hit by an uninsured driver or an unidentified (e.g. hit-and-run) driver.',
              textAlertContent:
                'We’ve decreased your Uninsured motorist bodily injury and Underinsured motorist bodily injury limits to match the changes you’ve made to your Bodily injury coverage.',
            },
          };
        },
        disclosureConfig: {
          disclosureText:
            'For your protection California law requires the following disclosure: Any person who knowingly presents false or fraudulent information to obtain or amend insurance coverage or to make a claim for the payment of a loss is guilty of a crime and may be subject to fines and confinement in state prison.',
        },
        discountsConfig: (discountsConfig: TConfig['discountsConfig']) => {
          return {
            ...discountsConfig,
          };
        },
        enabledSideEffects: ['ENABLE_CEQ_ON_COMP_OR_COLL'],
        driverFormConfig: (
          parentDriverFormConfig: TConfig['driverFormConfig']
        ) => ({
          ...parentDriverFormConfig,
          additionalFields: ['occupation', 'has_degree_or_license'],
          distantStudentConfig: {
            ...parentDriverFormConfig.distantStudentConfig,
            label: ({ firstName }: { firstName: string }) =>
              `Is ${
                firstName || 'this person'
              } a full-time student away at college?`,
          },
          goodStudentConfig: {
            ...parentDriverFormConfig.goodStudentConfig,
            label: ({ firstName }: { firstName: string }) =>
              `Is ${
                firstName || 'this person'
              } a full-time student with good academic standing?`,
          },
        }),
        vehicleFormConfig: () => ({
          additionalFields: [],
        }),
        driverManagementInfoModalConfig: () => ({
          infoTitle: 'Drivers',
          infoText:
            "Anyone who is 14 years of age or older, has a valid and active drivers license, and has regular access to the car(s) on your policy (i.e., 30 days/year or more), even if they are temporarily living away from home or are long-term guests, should be listed on your policy. If they're not, they will have limited coverage when driving a car on your policy. Note: Toggle does not provide financial responsibility filings (also known as an SR-22 or FR-44).",
        }),
        matureDriver: () => ({
          getSubjectText: null,
          label:
            'Has this driver completed a mature driver training course in the past 36 months?',
          explanation:
            'A certificate showing completion within the most recent 36 months of a Mature Driver Improvement Course approved by the California Department of Motor Vehicles.',
        }),
        visibleCoverages: (visibleCoverages: TConfig['visibleCoverages']) => {
          return {
            ...visibleCoverages,
            vehicleCoverages: [
              {
                label: 'Comprehensive',
                key: 'comprehensive_deductible',
                overrideValueLabel: 'Deductible',
              },
              {
                label: 'Collision',
                key: 'collision_deductible',
                overrideValueLabel: 'Deductible',
              },
              {
                label: 'Collision Deductible Waiver',
                key: 'collision_deductible_waiver',
                overrideValueLabel: '',
              },
              {
                label: 'Uninsured Motorist Property Damage',
                key: 'uninsured_motorist_property_damage_limit',
              },
            ],
            protectionCoverages: [
              ...visibleCoverages.protectionCoverages.filter(
                ({ key }) => !['pet_passenger'].includes(key)
              ),
            ],
          };
        },
        modalRemoveDriverConfig: (
          modalRemoveDriverConfig: TConfig['modalRemoveDriverConfig']
        ) => ({
          ...modalRemoveDriverConfig,
          subheader:
            'If removed, they will no longer be listed on your policy. All persons who reside with you and are licensed to drive, must be identified with no exceptions. Failure to disclose Household drivers or other misrepresentation of material facts may result in voiding, removal or reduction in coverage. Do you want to proceed?',
        }),
        vehicleManagementCopyConfig: () => ({
          subheader:
            'Feel free to edit the vehicle information shown below or add additional vehicles.',
          driverSubheader:
            'Feel free to edit the driver information shown below or add additional drivers. Also, please double check that all driver info is correct before moving on.',
        }),
        vehicleSideEffectMessages: () => ({
          collision_deductible_waiver:
            'This coverage is only available if Collision is also selected.',
          additional_equipment_on:
            'Because you already have Comprehensive or Collision coverage selected, Additional Equipment Coverage is required.',
          additional_equipment_off:
            'Additional equipment coverage is only available if your vehicle has either Comprehensive or Collision Coverage.',
          rental_reimbursement:
            'This coverage is only available if Comprehensive and Collision are also selected.',
          collision_deductible:
            'Because you already have Uninsured Motorist Property Damage selected, Collision is unavailable.',
          uninsured_motorist_property_damage_limit:
            'Because you already have Collision selected, Uninsured Motorist Property Damage is unavailable.',
        }),
        initialCoverageValues: () => ({
          collision_deductible: 'null',
          comprehensive_deductible: 'null',
          uninsured_motorist_property_damage_limit: '0',
          collision_deductible_waiver: 'false',
          additional_equipment: 'null',
          rental_reimbursement: 'null',
        }),
        alwaysOnConfig: {
          selfServiceConfig: {
            subHeader:
              'These additional perks are included with every Toggle auto policy.',
          },
          quoteFlowConfig: {
            subHeader:
              'These additional perks are included with every Toggle auto policy.',
          },
        },
        alwaysOnCoverageValues: () => ({
          roadside_assistance: 'true',
        }),
        selfServiceConfig: {
          promoCardParagraphText: 'Get twice the convenience when you combo.',
        },
      }
    : {};
}
export default ToggleCAConfig;
